import { ReactNode } from 'react';

import { Box } from '../../common/ui/Box';
import { Checkbox } from '../Checkbox/Checkbox';
import { Legend } from '../Label/Label';

import { OptionsGrid } from './components/OptionsGrid';

export interface CheckboxGroupOption<Value extends string = string> {
    value: Value;
    label: ReactNode;
    disabled?: boolean;
}

interface CheckboxGroupProps<Value extends string = string> {
    label?: string;
    options: CheckboxGroupOption<Value>[];
    value: Value[];
    onChange: (value: Value[]) => void;
    disabled?: boolean;
    columns?: 1 | 2;
    truncateLabels?: boolean;
}

export const CheckboxGroup = <Value extends string = string>({
    label,
    options,
    value,
    onChange,
    disabled = false,
    columns = 1,
    truncateLabels = false,
}: CheckboxGroupProps<Value>) => {
    const selectedValues = new Set(value);

    const handleChange = (option: CheckboxGroupOption<Value>, checked: boolean) => {
        if (disabled || option.disabled) {
            return;
        }
        if (checked) {
            onChange(selectedValues.has(option.value) ? value : [...value, option.value]);
            return;
        }
        onChange(value.filter((selectedValue) => selectedValue !== option.value));
    };

    return (
        <Box as="fieldset" border={0} padding={0} paddingTop={label ? 12 : 0} margin={0} minWidth={0}>
            {label ? <Legend>{label}</Legend> : null}
            <Box paddingTop={label ? 4 : 0}>
                <OptionsGrid $columns={columns} $rowCount={Math.ceil(options.length / columns)}>
                    {options.map((option) => (
                        <Box key={option.value} display="flex" alignItems="center" minWidth={0} width="100%">
                            <Box minWidth={0} width="100%">
                                <Checkbox
                                    label={option.label}
                                    checked={selectedValues.has(option.value)}
                                    disabled={disabled || option.disabled}
                                    truncateLabel={truncateLabels}
                                    onChange={(event) => handleChange(option, event.target.checked)}
                                />
                            </Box>
                        </Box>
                    ))}
                </OptionsGrid>
            </Box>
        </Box>
    );
};
